import { useState } from 'react'
import { Library, ShieldCheck, UserMinus, Users } from 'lucide-react'
import LoadingOverlay from '../../components/LoadingOverlay'
import PageLoader from '../../components/PageLoader'
import ResultDialog from '../../components/ResultDialog'
import { useResource } from '../../lib/useResource'
import Tabs from '../../components/Tabs'
import AccessGate from './AccessGate'
import { LOGO_ON_BRAND_SRC } from './assets'
import { getInstallState } from './installService'
import { strings as S } from './strings'
import DummyDataTab from './tabs/DummyDataTab'
import LicenseDataTab from './tabs/LicenseDataTab'
import TopicsTab from './tabs/TopicsTab'
import UsersPurgeTab from './tabs/UsersPurgeTab'
import './InstallPage.css'

const TAB_ITEMS = [
  { id: 'license', label: S.tabs.license, icon: ShieldCheck },
  { id: 'dummyData', label: S.tabs.dummyData, icon: Users },
  { id: 'topics', label: S.tabs.topics, icon: Library },
  { id: 'usersPurge', label: S.tabs.usersPurge, icon: UserMinus },
]

const TAB_PANELS = {
  license: LicenseDataTab,
  dummyData: DummyDataTab,
  topics: TopicsTab,
  usersPurge: UsersPurgeTab,
}

export default function InstallPage() {
  /* Whether the passkey has already been accepted is the SERVER's answer — it holds a
     data-protected cookie for fifteen minutes. This screen used to decide it itself, in
     the browser, against a passkey compiled into the bundle. */
  const { data, loading, reload } = useResource((signal) => getInstallState(signal), [])
  const accessGranted = Boolean(data?.accessGranted)
  const [activeTab, setActiveTab] = useState('license')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  /** Runs one Install action behind the wait screen, then shows its outcome. */
  async function runTask(task) {
    setBusy(true)
    try {
      setResult(await task())
    } catch (error) {
      setResult({ result: false, message: error.message })
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <PageLoader full />
  }

  if (!accessGranted) {
    /* Re-asking the server rather than flipping a local flag: the cookie it just set is
       the only thing that makes the actions work, so this confirms it arrived. */
    return <AccessGate onGranted={reload} />
  }

  const ActivePanel = TAB_PANELS[activeTab]

  return (
    <div className="install">
      <aside className="install__rail">
        <div className="install__brand">
          <span className="install__logoFrame">
            <img className="install__logo" src={LOGO_ON_BRAND_SRC} alt={S.logoAlt} />
          </span>
        </div>

        <nav className="install__nav">
          <Tabs
            items={TAB_ITEMS}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel={S.pageTitle}
          />
        </nav>

        <p className="install__railFooter">{S.footer}</p>
      </aside>

      <div className="install__main">
        <header className="install__header">
          <h1 className="install__title">
            <bdi>{S.pageTitle}</bdi>
          </h1>
        </header>

        <div className="install__scroll">
          <div
            className="install__panel"
            key={activeTab}
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            <ActivePanel onRun={runTask} />
          </div>
        </div>
      </div>

      <LoadingOverlay open={busy} label={S.waitScreen} />
      <ResultDialog result={result} closeLabel={S.close} onClose={() => setResult(null)} />
    </div>
  )
}

